import {
  Box,
  Button,
  Card,
  CollapsibleSection,
  Flex,
  Grid,
  Heading,
  HStack,
  IconButton,
  InfoBox,
  Input,
  Stack,
  Text,
  Tooltip,
  VStack,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import { MdAdd, MdCheck, MdClose, MdDelete, MdInfo } from "react-icons/md";

interface UrlVisibilityEditorProps {
  includeUrls: string;
  excludeUrls: string;
  onChange: (includeUrls: string, excludeUrls: string) => void;
}

export const UrlVisibilityEditor: React.FC<UrlVisibilityEditorProps> = ({
  includeUrls,
  excludeUrls,
  onChange,
}) => {
  const { t } = useTranslation();

  // Parse JSON arrays or default to empty
  const [includePatterns, setIncludePatterns] = useState<string[]>([]);
  const [excludePatterns, setExcludePatterns] = useState<string[]>([]);
  const [newIncludePattern, setNewIncludePattern] = useState("");
  const [newExcludePattern, setNewExcludePattern] = useState("");
  const [testUrl, setTestUrl] = useState("");
  const [includeError, setIncludeError] = useState("");
  const [excludeError, setExcludeError] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  // Initialize patterns from props
  useEffect(() => {
    try {
      const parsedInclude = includeUrls ? JSON.parse(includeUrls) : [];
      setIncludePatterns(Array.isArray(parsedInclude) ? parsedInclude : []);
    } catch {
      setIncludePatterns([]);
    }

    try {
      const parsedExclude = excludeUrls ? JSON.parse(excludeUrls) : [];
      setExcludePatterns(Array.isArray(parsedExclude) ? parsedExclude : []);
    } catch {
      setExcludePatterns([]);
    }
  }, [includeUrls, excludeUrls]);

  // Notify parent of changes
  const notifyChange = (include: string[], exclude: string[]) => {
    const includeJson = include.length > 0 ? JSON.stringify(include) : "";
    const excludeJson = exclude.length > 0 ? JSON.stringify(exclude) : "";
    onChange(includeJson, excludeJson);
  };

  // Validate pattern
  const validatePattern = (pattern: string): string | null => {
    if (!pattern || pattern.trim() === "") {
      return t("app.widgets.urlVisibility.patternRequired");
    }
    if (!pattern.startsWith("/") && !pattern.startsWith("*")) {
      return t("app.widgets.urlVisibility.patternMustStartWithSlash");
    }
    if (pattern.includes(" ")) {
      return t("app.widgets.urlVisibility.patternNoSpaces");
    }
    return null;
  };

  // Add include pattern
  const addIncludePattern = () => {
    const error = validatePattern(newIncludePattern);
    if (error) {
      setIncludeError(error);
      return;
    }

    const updated = [...includePatterns, newIncludePattern];
    setIncludePatterns(updated);
    notifyChange(updated, excludePatterns);
    setNewIncludePattern("");
    setIncludeError("");
  };

  // Add exclude pattern
  const addExcludePattern = () => {
    const error = validatePattern(newExcludePattern);
    if (error) {
      setExcludeError(error);
      return;
    }

    const updated = [...excludePatterns, newExcludePattern];
    setExcludePatterns(updated);
    notifyChange(includePatterns, updated);
    setNewExcludePattern("");
    setExcludeError("");
  };

  // Remove include pattern
  const removeIncludePattern = (index: number) => {
    const updated = includePatterns.filter((_, i) => i !== index);
    setIncludePatterns(updated);
    notifyChange(updated, excludePatterns);
  };

  // Remove exclude pattern
  const removeExcludePattern = (index: number) => {
    const updated = excludePatterns.filter((_, i) => i !== index);
    setExcludePatterns(updated);
    notifyChange(includePatterns, updated);
  };

  // Match pattern logic (same as inject.ts)
  const matchesPattern = (path: string, pattern: string): boolean => {
    const normalizedPath = path.toLowerCase().replace(/^\/|\/$/g, "");
    const normalizedPattern = pattern.toLowerCase().replace(/^\/|\/$/g, "");

    // Convert pattern to regex
    const regexPattern = normalizedPattern
      .split("/")
      .map((segment) =>
        segment === "*"
          ? "[^/]+"
          : segment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
      )
      .join("/");

    const regex = new RegExp(`^${regexPattern}$`, "i");
    return regex.test(normalizedPath);
  };

  // Test URL visibility
  const testUrlVisibility = (): {
    visible: boolean;
    matchedPattern?: string;
    reason: string;
  } => {
    if (!testUrl) {
      return { visible: true, reason: t("app.widgets.urlVisibility.noMatch") };
    }

    // Extract pathname from full URL or use as-is if already a path
    let pathToTest = testUrl;
    try {
      // Try to parse as full URL
      if (testUrl.startsWith("http://") || testUrl.startsWith("https://")) {
        const url = new URL(testUrl);
        pathToTest = url.pathname;
      }
    } catch {
      // Not a valid URL, treat as path
      pathToTest = testUrl;
    }

    // Normalize path (ensure it starts with /)
    const normalizedTestUrl = pathToTest.startsWith("/")
      ? pathToTest
      : `/${pathToTest}`;

    // Check include patterns first
    if (includePatterns.length > 0) {
      const matchedInclude = includePatterns.find((pattern) =>
        matchesPattern(normalizedTestUrl, pattern),
      );
      if (!matchedInclude) {
        return {
          visible: false,
          reason: t("app.widgets.urlVisibility.priorityLogic"),
        };
      }
    }

    // Check exclude patterns
    const matchedExclude = excludePatterns.find((pattern) =>
      matchesPattern(normalizedTestUrl, pattern),
    );
    if (matchedExclude) {
      return {
        visible: false,
        matchedPattern: matchedExclude,
        reason: `${t("app.widgets.urlVisibility.matchedPattern")}: ${matchedExclude}`,
      };
    }

    return {
      visible: true,
      reason:
        includePatterns.length > 0
          ? `${t("app.widgets.urlVisibility.matchedPattern")}: ${includePatterns.find((p) => matchesPattern(normalizedTestUrl, p))}`
          : t("app.widgets.urlVisibility.noMatch"),
    };
  };

  const testResult = testUrlVisibility();

  return (
    <CollapsibleSection
      pre="Visibility"
      title={t("app.widgets.urlVisibility.title")}
      description={t("app.widgets.urlVisibility.description")}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <Grid templateColumns={{ base: "1fr", lg: "1fr 400px" }} gap={6}>
        {/* Left Panel - Pattern Editor */}
        <Stack gap={6}>
          {/* Include Patterns */}
          <Box>
            <Flex alignItems="center" gap={2} mb={2}>
              <Text fontWeight="semibold">
                {t("app.widgets.urlVisibility.includePatterns")}
              </Text>
              <Tooltip
                content={t("app.widgets.urlVisibility.emptyIncludeTooltip")}
              >
                <Box as="span" cursor="help">
                  <MdInfo />
                </Box>
              </Tooltip>
            </Flex>

            <HStack mb={2}>
              <Input
                value={newIncludePattern}
                onChange={(e: any) => {
                  setNewIncludePattern(e.target.value);
                  if (includeError) setIncludeError("");
                }}
                placeholder={t("app.widgets.urlVisibility.patternPlaceholder")}
                onKeyPress={(e: any) =>
                  e.key === "Enter" && addIncludePattern()
                }
              />
              <Button onClick={addIncludePattern} colorScheme="blue">
                <MdAdd /> {t("app.widgets.urlVisibility.addPattern")}
              </Button>
            </HStack>

            {includeError && (
              <Text color="red.500" fontSize="sm" mb={2}>
                <MdClose style={{ display: "inline" }} /> {includeError}
              </Text>
            )}

            <Stack gap={2}>
              {includePatterns.map((pattern, index) => (
                <HStack key={index} bg="bg.muted" p={2} borderRadius="md">
                  <Text flex={1} fontFamily="mono" fontSize="sm">
                    {pattern}
                  </Text>
                  <IconButton
                    aria-label={t("app.widgets.urlVisibility.deletePattern")}
                    size="sm"
                    onClick={() => removeIncludePattern(index)}
                  >
                    <MdDelete />
                  </IconButton>
                </HStack>
              ))}
            </Stack>
          </Box>

          {/* Exclude Patterns */}
          <Box>
            <Flex alignItems="center" gap={2} mb={2}>
              <Text fontWeight="semibold">
                {t("app.widgets.urlVisibility.excludePatterns")}
              </Text>
              <Tooltip
                content={t("app.widgets.urlVisibility.emptyExcludeTooltip")}
              >
                <Box as="span" cursor="help">
                  <MdInfo />
                </Box>
              </Tooltip>
            </Flex>

            <HStack mb={2}>
              <Input
                value={newExcludePattern}
                onChange={(e: any) => {
                  setNewExcludePattern(e.target.value);
                  if (excludeError) setExcludeError("");
                }}
                placeholder={t("app.widgets.urlVisibility.patternPlaceholder")}
                onKeyPress={(e: any) =>
                  e.key === "Enter" && addExcludePattern()
                }
              />
              <Button onClick={addExcludePattern} colorScheme="blue">
                <MdAdd /> {t("app.widgets.urlVisibility.addPattern")}
              </Button>
            </HStack>

            {excludeError && (
              <Text color="red.500" fontSize="sm" mb={2}>
                <MdClose style={{ display: "inline" }} /> {excludeError}
              </Text>
            )}

            <Stack gap={2}>
              {excludePatterns.map((pattern, index) => (
                <HStack key={index} bg="bg.muted" p={2} borderRadius="md">
                  <Text flex={1} fontFamily="mono" fontSize="sm">
                    {pattern}
                  </Text>
                  <IconButton
                    aria-label={t("app.widgets.urlVisibility.deletePattern")}
                    size="sm"
                    onClick={() => removeExcludePattern(index)}
                  >
                    <MdDelete />
                  </IconButton>
                </HStack>
              ))}
            </Stack>
          </Box>
        </Stack>

        {/* Right Panel - Preview */}
        <Box>
          <Card.Root bg="bg.subtle">
            <Card.Header>
              <Heading size="sm">
                {t("app.widgets.urlVisibility.testUrl")}
              </Heading>
            </Card.Header>
            <Card.Body>
              <VStack alignItems="stretch" gap={4}>
                {/* Test URL */}
                <Box>
                  <Input
                    value={testUrl}
                    onChange={(e: any) => setTestUrl(e.target.value)}
                    placeholder={t(
                      "app.widgets.urlVisibility.testUrlPlaceholder",
                    )}
                    size="sm"
                  />
                </Box>

                {/* Test Result */}
                {testUrl && (
                  <InfoBox variant={testResult.visible ? "success" : "error"}>
                    <Flex alignItems="center" gap={2} mb={1}>
                      {testResult.visible ? (
                        <MdCheck color="green" />
                      ) : (
                        <MdClose color="red" />
                      )}
                      <Text fontWeight="semibold" fontSize="sm">
                        {testResult.visible
                          ? t("app.widgets.urlVisibility.widgetVisible")
                          : t("app.widgets.urlVisibility.widgetHidden")}
                      </Text>
                    </Flex>
                    <Text fontSize="xs" color="fg.muted">
                      {testResult.reason}
                    </Text>
                  </InfoBox>
                )}
              </VStack>
            </Card.Body>
          </Card.Root>
        </Box>
      </Grid>
    </CollapsibleSection>
  );
};
